import React, { useEffect, useState } from 'react';
import {
  ClipboardList,
  Inbox,
  ChevronLeft,
  CalendarClock,
  Loader2,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Card, CardContent } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import { ScrollArea } from '@/Components/ui/scroll-area';
import { cn } from '@/lib/utils';

/**
 * QuestionnaireResponsesViewer
 *
 * Two-mode dialog:
 *   1. List mode  — grid of candidate response cards.
 *   2. Detail mode — drill into a single questionnaire's Q/A pairs.
 *
 * The two views share the same Dialog wrapper and toggle via
 * `selectedQuestionnaire`.
 */
export default function QuestionnaireResponsesViewer({ jobId, onClose }) {
  const [responses, setResponses] = useState([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [detailedAnswers, setDetailedAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Initial fetch — list of questionnaires for this job.
  useEffect(() => {
    let cancelled = false;
    const fetchResponses = async () => {
      try {
        const res = await fetch(`/api/jobs/${jobId}/questionnaire-responses`);
        const data = await res.json();
        if (!cancelled && res.ok) setResponses(data.responses || []);
      } catch (err) {
        console.error('Error fetching responses:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchResponses();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const viewDetails = async (questionnaireId) => {
    setLoadingDetails(true);
    setSelectedQuestionnaire(questionnaireId);
    try {
      const res = await fetch(`/api/questionnaire/${questionnaireId}/details`);
      const data = await res.json();
      if (res.ok) setDetailedAnswers(data.answers || []);
    } catch (err) {
      console.error('Error fetching details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetails = () => {
    setSelectedQuestionnaire(null);
    setDetailedAnswers([]);
  };

  // Status → tone for the per-card badge.
  const getStatusTone = (status) =>
    ({
      submitted:
        'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
      in_progress:
        'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
      pending:
        'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
    }[status] || 'bg-slate-100 text-slate-700');

  const getStatusLabel = (status) =>
    ({
      submitted: 'Submitted',
      in_progress: 'In Progress',
      pending: 'Pending',
    }[status] || 'Pending');

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Questionnaire Responses
          </DialogTitle>
        </DialogHeader>

        {selectedQuestionnaire ? (
          // -------------------------------------------------- detail view
          <>
            <div className="mb-2">
              <Button variant="ghost" size="sm" onClick={closeDetails}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back to list
              </Button>
            </div>

            <ScrollArea className="max-h-[60vh] pr-3">
              {loadingDetails ? (
                <div className="space-y-3 py-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full rounded-lg" />
                  ))}
                </div>
              ) : detailedAnswers.length === 0 ? (
                <EmptyState text="No answers found" />
              ) : (
                <div className="space-y-3">
                  {detailedAnswers.map((answer, idx) => (
                    <Card
                      key={idx}
                      className="border border-border/40 shadow-none"
                    >
                      <CardContent className="space-y-2 p-4">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className="bg-primary/10 text-[10px] uppercase text-primary"
                          >
                            Q{idx + 1}
                          </Badge>
                          {answer.is_required && (
                            <Badge
                              variant="outline"
                              className="bg-rose-100 text-[10px] uppercase text-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
                            >
                              Required
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium">
                          {answer.question_text}
                        </p>
                        <div className="rounded-md bg-muted/40 p-3 text-sm">
                          {answer.answer_text || (
                            <span className="italic text-muted-foreground">
                              No answer provided
                            </span>
                          )}
                        </div>
                        {answer.answered_at && (
                          <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <CalendarClock className="h-3 w-3" />
                            Answered:{' '}
                            {new Date(answer.answered_at).toLocaleString()}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          // -------------------------------------------------- list view
          <ScrollArea className="max-h-[60vh] pr-3">
            {loading ? (
              <div className="grid gap-3 py-2 sm:grid-cols-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-44 w-full rounded-lg" />
                ))}
              </div>
            ) : responses.length === 0 ? (
              <EmptyState text="No questionnaire responses yet" />
            ) : (
              <div className="grid gap-3 py-2 sm:grid-cols-2">
                {responses.map((response) => {
                  const completionRate =
                    response.total_questions > 0
                      ? Math.round(
                          (response.answered_questions /
                            response.total_questions) *
                            100,
                        )
                      : 0;

                  return (
                    <Card
                      key={response.questionnaire_id}
                      className="border border-border/40 shadow-sm"
                    >
                      <CardContent className="space-y-3 p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold">
                              {response.candidate_name}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {response.candidate_email}
                            </p>
                          </div>
                          <Badge
                            variant="outline"
                            className={cn(
                              'shrink-0 border-0 text-[10px]',
                              getStatusTone(response.status),
                            )}
                          >
                            {getStatusLabel(response.status)}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="rounded-md border border-border/40 p-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              Questions
                            </div>
                            <div className="text-sm font-semibold tabular-nums">
                              {response.answered_questions} /{' '}
                              {response.total_questions}
                            </div>
                          </div>
                          <div className="rounded-md border border-border/40 p-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              Completion
                            </div>
                            <div className="text-sm font-semibold tabular-nums">
                              {completionRate}%
                            </div>
                          </div>
                        </div>

                        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary transition-all"
                            style={{ width: `${completionRate}%` }}
                          />
                        </div>

                        {response.submitted_at && (
                          <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <CalendarClock className="h-3 w-3" />
                            Submitted:{' '}
                            {new Date(response.submitted_at).toLocaleString()}
                          </p>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => viewDetails(response.questionnaire_id)}
                          disabled={response.status === 'pending'}
                        >
                          {response.status === 'pending'
                            ? 'Not Started'
                            : 'View Answers'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <Inbox className="h-10 w-10 text-muted-foreground/50" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
